import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Zap, Crown, Building2, Check,
  CreditCard, Receipt, Loader2, ArrowRight, ExternalLink, RefreshCw, Ban,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const PLAN_ICONS = {
  free: Sparkles,
  interview_ready_pass: Zap,
  career_pro: Crown,
  career_enterprise: Building2,
};

function formatDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function daysRemaining(iso) {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="mb-8">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-zinc-50 tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [salesEmail, setSalesEmail] = useState("hello@intuiterview.com");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelComment, setCancelComment] = useState("");

  const CANCEL_REASONS = [
    { id: "got_the_job", label: "I got the job 🎉" },
    { id: "too_expensive", label: "Too expensive" },
    { id: "not_using", label: "Not using it enough" },
    { id: "missing_features", label: "Missing features I need" },
    { id: "not_working", label: "Something didn't work well" },
    { id: "other", label: "Other" },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, p, h] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/plans"),
        api.get("/billing/history"),
      ]);
      setStatus(s.data);
      setPlans(p.data.plans);
      setSalesEmail(p.data.sales_email);
      setHistory(h.data.history || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load billing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doCheckout = async (planId) => {
    setActioning(planId);
    setMsg("");
    setErr("");
    try {
      const { data } = await api.post(`/billing/checkout/${planId}`, {
        return_url: window.location.origin,
      });
      if (data.kind === "checkout_url" && data.url) {
        // Redirect to Stripe Checkout. On success/cancel Stripe will send the
        // user back to /payment/success or /payment/cancel - see App.jsx.
        window.location.assign(data.url);
        return;
      }
      if (data.kind === "contact") {
        window.location.href = data.next_url;
      } else if (data.kind === "beta_activated") {
        setMsg(`Activated. ${data.message}`);
        await load();
      } else {
        setMsg(data.message || "Request received.");
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setActioning(null);
    }
  };

  const doCancel = async () => {
    setConfirmCancelOpen(false);
    setActioning("cancel");
    setErr("");
    try {
      await api.post("/billing/cancel", {
        reason: cancelReason || null,
        comment: cancelComment || null,
      });
      setCancelReason("");
      setCancelComment("");
      setMsg("Subscription cancelled. You've been reverted to the Free plan.");
      await load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Cancel failed.");
    } finally {
      setActioning(null);
    }
  };

  if (loading || !status) {
    return (
      <div data-testid="billing-loading" className="flex items-center justify-center py-20 text-zinc-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading billing…
      </div>
    );
  }

  const currentPlan = status.plan;
  const sub = status.subscription;
  const usage = status.usage;
  const CurrentIcon = PLAN_ICONS[currentPlan.id] || Sparkles;
  const remaining = daysRemaining(sub.expires_at);
  const passActive = sub.plan_id === "interview_ready_pass" && sub.status === "active";
  const proActive = sub.plan_id === "career_pro" && sub.status === "active";
  const enterpriseActive = sub.plan_id === "career_enterprise";
  const paidActive = passActive || proActive || enterpriseActive;

  const passPlan = plans.find((p) => p.id === "interview_ready_pass");
  const proPlan = plans.find((p) => p.id === "career_pro");
  const enterprisePlan = plans.find((p) => p.id === "career_enterprise");

  return (
    <div data-testid="billing-page" className="max-w-4xl">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">Billing</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-50 mt-1">
            Plan &amp; Billing
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Manage your Intuiterview plan, usage, and history.
          </p>
        </div>
        <Link
          to="/pricing"
          data-testid="billing-compare-plans-top"
          className="inline-flex flex-shrink-0 items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/25 hover:border-amber-500/40 bg-amber-500/[0.04] rounded-lg px-3 py-2 transition-colors"
        >
          <Receipt className="h-3 w-3" /> Compare Plans
        </Link>
      </div>

      {msg && (
        <div data-testid="billing-msg" className="mb-6 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2">
          {msg}
        </div>
      )}
      {err && (
        <div data-testid="billing-err" className="mb-6 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {/* Current Plan */}
      <Section title="Current Plan" testid="billing-current-plan">
        <div className="p-6 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-lg bg-zinc-800/70 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                <CurrentIcon className="h-5 w-5 text-amber-400" />
              </span>
              <div>
                <div className="text-lg font-bold text-zinc-50 tracking-tight">
                  {currentPlan.name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {currentPlan.price_display}
                  {sub.status === "active" && sub.started_at && (
                    <> · Active since {formatDate(sub.started_at)}</>
                  )}
                  {sub.status === "cancelled" && <> · Cancelled</>}
                </div>
                {sub.expires_at && (
                  <div className="text-[11px] text-amber-300 mt-1">
                    Expires {formatDate(sub.expires_at)}
                    {remaining !== null && <> ({remaining} days remaining)</>}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {!proActive && !enterpriseActive && proPlan && (
                  <button
                    onClick={() => doCheckout("career_pro")}
                    data-testid="billing-cta-pro"
                    disabled={actioning === "career_pro"}
                    className="inline-flex items-center gap-2 text-sm border border-zinc-700 hover:border-zinc-500 text-zinc-100 rounded-lg px-4 py-2 disabled:opacity-60"
                  >
                    {actioning === "career_pro" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="h-3.5 w-3.5" />}
                    Upgrade to Career Pro
                  </button>
                )}
                {paidActive && !enterpriseActive && (
                  <button
                    onClick={() => setConfirmCancelOpen(true)}
                    data-testid="billing-cta-cancel"
                    disabled={actioning === "cancel"}
                    className="inline-flex items-center gap-2 text-sm border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-lg px-4 py-2 disabled:opacity-60"
                  >
                    {actioning === "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                    Manage / Cancel
                  </button>
                )}
              </div>
              {paidActive && !enterpriseActive && (
                <Link
                  to="/pricing"
                  data-testid="billing-change-plan"
                  className="text-xs text-zinc-500 hover:text-zinc-200 underline underline-offset-4 sm:text-right"
                >
                  Change Plan
                </Link>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Usage */}
      <Section title="Usage" subtitle="This month, on your account." testid="billing-usage">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: "mock_sessions_this_month", label: "Mock Sessions", value: usage.mock_sessions_this_month },
            { key: "resume_alignments_this_month", label: "Resume Improvements", value: usage.resume_alignments_this_month },
            { key: "interview_day_briefs_total", label: "Interview Day Briefs", value: usage.interview_day_briefs_total },
            { key: "resumes_total", label: "Resumes Stored", value: usage.resumes_total },
          ].map((s) => (
            <div key={s.key} className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/40">
              <div className="text-2xl font-bold text-zinc-50">{s.value}</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Upgrade prompts */}
      {!paidActive && passPlan && (
        <Section title="Unlock more" testid="billing-upsell">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/[0.06] via-zinc-900/40 to-zinc-950/60 shadow-[0_0_30px_-14px_rgba(245,158,11,0.4)]">
              <div className="flex items-center gap-2.5">
                <Zap className="h-4 w-4 text-amber-400" />
                <div className="text-sm font-semibold text-zinc-50">Interview Ready Pass</div>
                <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-full px-2 py-0.5">
                  Most popular
                </span>
              </div>
              <div className="mt-3 text-3xl font-bold text-zinc-50">$0</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">Free during beta · 30 days · 1 workspace</div>
              <div className="text-xs text-zinc-400 mt-3 leading-relaxed">
                Everything you need to walk into one specific interview fully prepared.
              </div>
              <button
                onClick={() => doCheckout("interview_ready_pass")}
                data-testid="billing-buy-pass"
                disabled={actioning === "interview_ready_pass"}
                className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2.5 text-sm disabled:opacity-60"
              >
                {actioning === "interview_ready_pass" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                Try Interview Ready
              </button>
            </div>

            {proPlan && (
              <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/40">
                <div className="flex items-center gap-2.5">
                  <Crown className="h-4 w-4 text-amber-400" />
                  <div className="text-sm font-semibold text-zinc-50">Career Pro</div>
                </div>
                <div className="mt-3 text-3xl font-bold text-zinc-50">$0</div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">Free during beta · Unlimited everything</div>
                <div className="text-xs text-zinc-400 mt-3 leading-relaxed">
                  Unlimited interview workspaces, mock sessions, briefs, and progress tracking.
                </div>
                <button
                  onClick={() => doCheckout("career_pro")}
                  data-testid="billing-upgrade-pro"
                  disabled={actioning === "career_pro"}
                  className="mt-4 inline-flex items-center justify-center gap-2 w-full border border-zinc-700 hover:border-zinc-500 text-zinc-100 rounded-lg px-4 py-2.5 text-sm disabled:opacity-60"
                >
                  {actioning === "career_pro" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                  Try Career Pro
                </button>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Contact Sales / Enterprise */}
      {enterprisePlan && !enterpriseActive && (
        <Section title="For teams" subtitle="Universities, recruiters, career centers, and companies." testid="billing-enterprise">
          <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-zinc-800/70 border border-zinc-800 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-amber-400" />
              </span>
              <div>
                <div className="text-sm font-semibold text-zinc-50">Career Enterprise</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Coming Soon · indicative future pricing from $199/month · Enterprise features are under development and are not currently available
                </div>
              </div>
            </div>
            <a
              href={`mailto:${salesEmail}?subject=Intuiterview%20Enterprise%20inquiry`}
              data-testid="billing-contact-sales"
              className="inline-flex items-center gap-2 text-sm bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2"
            >
              Contact Sales <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </Section>
      )}

      {/* History */}
      <Section title="Billing History" subtitle="Recent activity on your account." testid="billing-history">
        {history.length === 0 ? (
          <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/30 text-xs text-zinc-500">
            No billing activity yet. Your invoices and activations will appear here.
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/60">
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Plan / Item</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, idx) => (
                  <tr key={h.intent_id || idx} className="border-t border-zinc-800/70">
                    <td className="px-4 py-2.5 text-zinc-400">{formatDate(h.created_at)}</td>
                    <td className="px-4 py-2.5 text-zinc-100">{h.description || h.plan_id}</td>
                    <td className="px-4 py-2.5 text-zinc-500 capitalize">{(h.kind || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-300">
                      {h.amount_cents ? `$${(h.amount_cents / 100).toFixed(2)}` : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Cancel confirmation (in-app dialog, native confirm() is blocked in some embedded browsers) */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent data-testid="billing-cancel-dialog" className="border-zinc-800 bg-zinc-950 text-zinc-50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Cancel your subscription and revert to the Free plan? You&apos;ll lose access to paid features immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="text-xs font-medium text-zinc-300">Before you go, why are you cancelling?</div>
            <div className="flex flex-wrap gap-2">
              {CANCEL_REASONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`cancel-reason-${r.id}`}
                  onClick={() => setCancelReason(cancelReason === r.id ? "" : r.id)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    cancelReason === r.id
                      ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <textarea
              data-testid="cancel-feedback-comment"
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Anything we could have done better? (optional)"
              className="w-full text-xs rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-200 placeholder:text-zinc-600 px-3 py-2 focus:outline-none focus:border-zinc-600 resize-none"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="billing-cancel-dialog-keep"
              className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-zinc-50"
            >
              Keep my plan
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-cancel-dialog-confirm"
              onClick={doCancel}
              className="bg-rose-600 text-white hover:bg-rose-500"
            >
              Yes, cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Refresh */}
      <div className="mt-2">
        <button
          onClick={load}
          data-testid="billing-refresh"
          className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-200"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="mt-8 text-[11px] text-zinc-600 leading-relaxed">
        Payment gateway launching soon. Beta activations don&apos;t charge your card.
        By activating a paid plan you agree to our{" "}
        <Link className="text-zinc-400 hover:text-zinc-200 underline underline-offset-4" to="/trust/terms">Terms of Service</Link>.
      </div>
    </div>
  );
}
